// Lightweight markdown renderer for blog posts.
// Supports: ### sub-headings, paragraphs, bullet lists (- ...), numbered lists (1. ...),
// **bold** inline markers and [text](url) inline links.

import React from "react";
import { Link } from "react-router-dom";

// Inline tokenizer that recognises **bold** and [text](url) markdown links.
// Everything else falls through as plain text and continues to render exactly
// as it did before this addition (existing posts unaffected).
function renderInline(text) {
  const tokenRe = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;
  const parts = text.split(tokenRe);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="text-[#01080E]">
          {p.slice(2, -2)}
        </strong>
      );
    }
    const link = p.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const label = link[1];
      const href = link[2];
      // Internal Thoratech paths (start with / but not //) render through
      // react-router for client-side navigation. Everything else falls back to
      // a safe external anchor.
      if (href.startsWith("/") && !href.startsWith("//")) {
        return (
          <Link
            key={i}
            to={href}
            className="text-[#2AC0A5] underline underline-offset-2 hover:text-[#01080E] transition-colors"
          >
            {label}
          </Link>
        );
      }
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#2AC0A5] underline underline-offset-2 hover:text-[#01080E] transition-colors"
        >
          {label}
        </a>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function MarkdownBody({ body }) {
  const lines = body.split("\n");
  const blocks = [];
  let buffer = [];
  let mode = null; // null | 'ul' | 'ol' | 'p'

  const flush = () => {
    if (buffer.length === 0) return;
    if (mode === "p") {
      blocks.push({ type: "p", lines: buffer });
    } else if (mode === "ul") {
      blocks.push({ type: "ul", lines: buffer });
    } else if (mode === "ol") {
      blocks.push({ type: "ol", lines: buffer });
    }
    buffer = [];
    mode = null;
  };

  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    if (t.startsWith("#### ")) {
      flush();
      blocks.push({ type: "h3", text: t.slice(5) });
      continue;
    }
    if (t.startsWith("### ")) {
      flush();
      blocks.push({ type: "h", text: t.slice(4) });
      continue;
    }
    const img = t.match(/^!\[([^\]]*)\]\(([^)\s|]+)(?:\|([^)\s]+))?\)$/);
    if (img) {
      flush();
      blocks.push({ type: "img", alt: img[1], src: img[2], mobileSrc: img[3] || null });
      continue;
    }
    const bullet = t.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      if (mode !== "ul") flush();
      mode = "ul";
      buffer.push(bullet[1]);
      continue;
    }
    const num = t.match(/^\d+\.\s+(.+)$/);
    if (num) {
      if (mode !== "ol") flush();
      mode = "ol";
      buffer.push(num[1]);
      continue;
    }
    if (mode !== "p") flush();
    mode = "p";
    buffer.push(t);
  }
  flush();

  return (
    <div className="space-y-6">
      {blocks.map((b, i) => {
        if (b.type === "h") {
          return (
            <h2
              key={i}
              className="font-display text-2xl md:text-3xl font-bold text-[#01080E] mt-8 leading-tight"
            >
              {b.text}
            </h2>
          );
        }
        if (b.type === "h3") {
          return (
            <h3
              key={i}
              className="font-display text-xl md:text-2xl font-semibold text-[#01080E] mt-4 leading-tight"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "img") {
          return (
            <figure key={i} className="my-10 border border-[#01080E]/10" data-testid="blog-inline-figure">
              <picture>
                {b.mobileSrc && <source media="(max-width: 767px)" srcSet={b.mobileSrc} />}
                <img src={b.src} alt={b.alt} className="w-full h-auto" loading="lazy" />
              </picture>
            </figure>
          );
        }
        if (b.type === "p") {
          return (
            <p
              key={i}
              className="text-[#01080E]/80 text-lg leading-relaxed"
            >
              {renderInline(b.lines.join(" "))}
            </p>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="space-y-3 list-none pl-0">
              {b.lines.map((l, j) => (
                <li
                  key={j}
                  className="flex items-start gap-3 text-[#01080E]/80 text-lg leading-relaxed"
                >
                  <span
                    aria-hidden
                    className="mt-2.5 inline-block h-2 w-2 bg-[#2AC0A5] flex-shrink-0"
                  />
                  <span>{renderInline(l)}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={i} className="space-y-3 list-none pl-0 counter-reset-list">
              {b.lines.map((l, j) => (
                <li
                  key={j}
                  className="flex items-start gap-4 text-[#01080E]/80 text-lg leading-relaxed"
                >
                  <span className="font-mono-tech text-sm font-semibold text-[#2AC0A5] mt-1 min-w-[24px]">
                    {String(j + 1).padStart(2, "0")}
                  </span>
                  <span>{renderInline(l)}</span>
                </li>
              ))}
            </ol>
          );
        }
        return null;
      })}
    </div>
  );
}
